import React from 'react';
import { CustomModal } from '../../../../common/CustomModal';
import '../../../../../styles/components/auction-client-pay.scss';
import { toast } from 'react-toastify';
import { Upload, User, IdCard } from 'lucide-react';

export class AddDriver extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      // Driver Basic Information
      name: '',
      contact_number: '',
      license_number: '',
      license_validity_date: '',
      id_number: '',
      
      // Files
      id_copy: null,
      driver_photo: null,
      
      // Status
      is_active: true,
      loading: false,
      submitting: false
    };
  }

  handleCancel = () => {
    this.props.onClose();
  };

  handleSubmit = async (e) => {
    e.preventDefault();
    this.setState({ submitting: true });

    // Validate required fields
    const requiredFields = {
      name: 'Driver name',
      contact_number: 'Contact number',
      license_number: 'License number',
      license_validity_date: 'License validity date',
      id_number: 'ID number'
    };

    const missingFields = Object.keys(requiredFields).filter(
      field => !this.state[field]
    );

    if (missingFields.length > 0) {
      toast.error(`Please fill all required fields: ${missingFields.map(field => requiredFields[field]).join(', ')}`);
      this.setState({ submitting: false });
      return;
    }

    // Validate license validity date
    if (this.state.license_validity_date) {
      const validityDate = new Date(this.state.license_validity_date);
      const today = new Date();
      if (validityDate < today) {
        toast.error('License validity date cannot be in the past');
        this.setState({ submitting: false });
        return;
      }
    }

    // Prepare form data
    const formData = new FormData();
    
    // Append basic information
    formData.append('name', this.state.name);
    formData.append('contact_number', this.state.contact_number);
    formData.append('license_number', this.state.license_number);
    formData.append('license_validity_date', this.state.license_validity_date);
    formData.append('id_number', this.state.id_number);
    formData.append('is_active', this.state.is_active);

    // Append files if selected
    if (this.state.id_copy) {
      formData.append('id_copy', this.state.id_copy);
    }
    
    if (this.state.driver_photo) {
      formData.append('driver_photo', this.state.driver_photo);
    }

    console.log('Driver Data:', {
      name: this.state.name,
      contact_number: this.state.contact_number,
      license_number: this.state.license_number,
      license_validity_date: this.state.license_validity_date,
      id_number: this.state.id_number,
      is_active: this.state.is_active,
      id_copy: this.state.id_copy?.name,
      driver_photo: this.state.driver_photo?.name
    });

    try {
      // Simulate API call - replace with actual API
      // const response = await createDriver(formData);
      const response = { status: true, message: 'Driver added successfully' };
      
      if (response.status) {
        toast.success(response.message || 'Driver added successfully');
        this.props.onSuccess?.();
        this.handleCancel();
      } else {
        toast.error(response.message || 'Failed to add driver');
      }
    } catch (error) {
      console.error('Error adding driver:', error);
      toast.error('Something went wrong while adding driver');
    } finally {
      this.setState({ submitting: false });
    }
  };

  handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    this.setState({
      [name]: type === 'checkbox' ? checked : value
    });
  };

  handleFileUpload = (field, e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/') && !file.type.includes('pdf')) {
        toast.error('Please select a valid image or PDF file');
        return;
      }

      // Validate file size (5MB max)
      if (file.size > 5 * 1024 * 1024) {
        toast.error('File size should be less than 5MB');
        return;
      }

      this.setState({ [field]: file });
    }
  };

  removeFile = (field) => {
    this.setState({ [field]: null });
  };

  render() {
    const { show } = this.props;
    const {
      name,
      contact_number,
      license_number,
      license_validity_date,
      id_number,
      id_copy,
      driver_photo,
      is_active,
      submitting
    } = this.state;

    return (
      <CustomModal
        isOpen={show}
        header="Add New Driver"
        onClose={this.handleCancel}
        size="lg"
      >
        <div className="driver-form">
          <form onSubmit={this.handleSubmit}>
            {/* Basic Information Section */}
            <div className="form-section">
              <h3 className="section-title">
                <User size={20} />
                Basic Information
              </h3>
              
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label required">Full Name</label>
                  <input
                    type="text"
                    name="name"
                    value={name}
                    onChange={this.handleInputChange}
                    className="form-input"
                    placeholder="Enter driver's full name"
                    required
                    disabled={submitting}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label required">Contact Number</label>
                  <input
                    type="tel"
                    name="contact_number"
                    value={contact_number}
                    onChange={this.handleInputChange}
                    className="form-input"
                    placeholder="Enter contact number"
                    required
                    disabled={submitting}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label required">License Number</label>
                  <input
                    type="text"
                    name="license_number"
                    value={license_number}
                    onChange={this.handleInputChange}
                    className="form-input"
                    placeholder="Enter license number"
                    required
                    disabled={submitting}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label required">License Validity Date</label>
                  <input
                    type="date"
                    name="license_validity_date"
                    value={license_validity_date}
                    onChange={this.handleInputChange}
                    className="form-input"
                    required
                    disabled={submitting}
                    min={new Date().toISOString().split('T')[0]}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label required">ID Number</label>
                  <input
                    type="text"
                    name="id_number"
                    value={id_number}
                    onChange={this.handleInputChange}
                    className="form-input"
                    placeholder="Enter ID number"
                    required
                    disabled={submitting}
                  />
                </div>

                <div className="form-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      name="is_active"
                      checked={is_active}
                      onChange={this.handleInputChange}
                      disabled={submitting}
                    />
                    <span className="checkmark"></span>
                    Active Driver
                  </label>
                </div>
              </div>
            </div>

            {/* Document Upload Section */}
            <div className="form-section">
              <h3 className="section-title">
                <IdCard size={20} />
                Documents & Photos
              </h3>

              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">ID Copy</label>
                  <div className="file-upload-group">
                    <div className="file-upload">
                      <input
                        type="file"
                        onChange={(e) => this.handleFileUpload('id_copy', e)}
                        accept="image/*,.pdf"
                        className="file-input"
                        id="id-copy"
                        disabled={submitting}
                      />
                      <label htmlFor="id-copy" className="file-label">
                        <Upload size={16} />
                        {id_copy ? id_copy.name : 'Upload ID Copy'}
                      </label>
                    </div>
                    {id_copy && (
                      <div className="file-preview">
                        <span>{id_copy.name}</span>
                        <button
                          type="button"
                          className="remove-file"
                          onClick={() => this.removeFile('id_copy')}
                          disabled={submitting}
                        >
                          ×
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">Driver Photo</label>
                  <div className="file-upload-group">
                    <div className="file-upload">
                      <input
                        type="file"
                        onChange={(e) => this.handleFileUpload('driver_photo', e)}
                        accept="image/*"
                        className="file-input"
                        id="driver-photo"
                        disabled={submitting}
                      />
                      <label htmlFor="driver-photo" className="file-label">
                        <Upload size={16} />
                        {driver_photo ? driver_photo.name : 'Upload Driver Photo'}
                      </label>
                    </div>
                    {driver_photo && (
                      <div className="file-preview">
                        <span>{driver_photo.name}</span>
                        <button
                          type="button"
                          className="remove-file"
                          onClick={() => this.removeFile('driver_photo')}
                          disabled={submitting}
                        >
                          ×
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Form Actions */}
            <div className="form-actions">
              <button
                type="button"
                className="button1 button1-primary"
                onClick={this.handleCancel}
                disabled={submitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn-new"
                disabled={submitting}
              >
                {submitting ? 'Adding Driver...' : 'Add Driver'}
              </button>
            </div>
          </form>
        </div>
      </CustomModal>
    );
  }
}